import { Link } from 'react-router-dom';
import { FiClock, FiExternalLink, FiHelpCircle } from 'react-icons/fi';
import CopyButton from '../components/transaction/CopyButton';
import SponsoredSection from '../components/SponsoredSection';
import { useTronBlock } from '../hooks/useTronBlock';
import { formatRelativeTime } from '../utils/formatTime';

function TronRow({ label, children, hint }) {
  return (
    <div className="ttx-row">
      <div className="ttx-row__label">
        {label}
        {hint ? (
          <span className="ttx-hint" title={hint}>
            <FiHelpCircle aria-hidden />
          </span>
        ) : null}
      </div>
      <div className="ttx-row__value">{children}</div>
    </div>
  );
}

function formatTronUtc(unixSeconds) {
  if (!unixSeconds) {
    return '—';
  }
  const iso = new Date(unixSeconds * 1000).toISOString();
  return `${iso.slice(0, 10)} ${iso.slice(11, 19)} (UTC)`;
}

function TronBlockDetailsView({ number }) {
  const { data, loading, error } = useTronBlock(number);

  if (loading) {
    return (
      <main className="sections ttx-page">
        <div className="stx-skeleton stx-skeleton-title" />
        <div className="stx-skeleton stx-skeleton-panel" />
      </main>
    );
  }

  if (error || !data) {
    return (
      <main className="sections ttx-page">
        <h1 className="ttx-title">Block Details</h1>
        <div className="stx-error">
          <p>{error || 'Block not found.'}</p>
          <Link to="/" className="ttx-addr">Back to home</Link>
        </div>
      </main>
    );
  }

  const explorerUrl = `https://tronscan.org/#/block/${data.blockNumber}`;

  return (
    <main className="sections ttx-page">
      <SponsoredSection />
      <h1 className="ttx-title">Block Details</h1>

      <section className="ttx-card">
        <TronRow label="Block:">
          <div className="ttx-inline">
            <span className="ttx-addr ttx-addr--bold">{data.blockNumber}</span>
            <CopyButton text={String(data.blockNumber)} className="ttx-copy-inline" />
            <a href={explorerUrl} target="_blank" rel="noreferrer" className="ttx-addr">
              View on Tronscan
              <FiExternalLink aria-hidden />
            </a>
          </div>
        </TronRow>

        <TronRow label="Block ID:">
          <div className="ttx-hash-line">
            <code className="ttx-mono">{data.blockId || '—'}</code>
            {data.blockId ? <CopyButton text={data.blockId} className="ttx-copy-inline" /> : null}
          </div>
        </TronRow>

        <TronRow label="Block &amp; Time:">
          <div className="ttx-inline">
            <span className="ttx-muted">{formatRelativeTime(data.blockTime)}</span>
            <span className="ttx-muted ttx-time-full">
              <FiClock aria-hidden />
              {formatTronUtc(data.blockTime)}
            </span>
          </div>
        </TronRow>

        <TronRow label="Witness:" hint="Block producer">
          <span>
            {data.witness?.name ? `${data.witness.name} ` : ''}
            {data.witness?.address ? (
              <Link to={`/lookup?q=${encodeURIComponent(data.witness.address)}`} className="ttx-addr">
                {data.witness.address}
              </Link>
            ) : (
              '—'
            )}
          </span>
        </TronRow>

        <TronRow label="Transactions:">
          <span>{data.transactionCount?.toLocaleString() ?? '—'}</span>
        </TronRow>
      </section>

      {data.transactions?.length > 0 ? (
        <section className="ttx-card">
          <h2 className="stx-card-heading">Transactions in this block</h2>
          <div className="stx-block-tx-list">
            {data.transactions.map((tx) => (
              <Link
                key={tx.hash}
                to={`/tx/${encodeURIComponent(tx.hash)}`}
                className="stx-block-tx-item stx-block-tx-item--tron"
              >
                <code>{tx.displayHash}</code>
                <span className="stx-block-tx-type">{tx.type}</span>
                {tx.success ? <span className="stx-badge stx-badge--success">OK</span> : null}
              </Link>
            ))}
          </div>
          {data.transactionCount > data.transactions.length ? (
            <p className="ttx-muted stx-block-tx-more">
              Showing {data.transactions.length} of {data.transactionCount.toLocaleString()} transactions.
            </p>
          ) : null}
        </section>
      ) : null}
    </main>
  );
}

export default TronBlockDetailsView;
